import React, { useState, useEffect, useCallback } from 'react';
import {
  Trophy, Plus, Calendar, Users, Camera, DollarSign, X, Check,
  Clock, Play, Loader2, Globe, Building2, Edit2, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const STATUS_COLOR = {
  upcoming: 'bg-blue-100 text-blue-700',
  live: 'bg-green-100 text-green-700',
  ended: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-red-100 text-red-600',
};

const SUBJECTS = ['Mathematics', 'English Language', 'Basic Science', 'Social Studies', 'Agricultural Science', 'Economics', 'Government', 'Literature', 'Chemistry', 'Physics', 'Biology', 'Geography'];

function CompModal({ comp, onClose, onSaved }) {
  const [form, setForm] = useState(comp || {
    title: '', subject: 'Mathematics', description: '',
    scheduled_at: '', duration_minutes: 30, question_count: 20,
    scope: 'school', target_school_id: '',
    requires_camera: false, is_free: true, entry_fee: 0,
  });
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    api.get('/master/schools').then(r => { if (r?.success) setSchools(r.data || []); }).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.scheduled_at) { toast.error('Schedule date required'); return; }
    setSaving(true);
    try {
      let res;
      if (comp?.id) res = await api.put(`/quiz/master/competitions/${comp.id}`, form);
      else res = await api.post('/quiz/master/competitions', form);
      if (res?.success) { toast.success(comp?.id ? 'Competition updated' : 'Competition created'); onSaved(); }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="comp-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-slate-800">{comp?.id ? 'Edit Competition' : 'Schedule Competition'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
        </div>
        <div className="space-y-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-200 outline-none"
            placeholder="Competition title *" data-testid="comp-title-input" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Subject</label>
              <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Scope</label>
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none"
                data-testid="comp-scope-select">
                <option value="school">Single School</option>
                <option value="multi_school">Random Students (Cross-EDUVA)</option>
                <option value="global">All EDUVA Schools</option>
              </select>
            </div>
          </div>

          {form.scope === 'school' && (
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Target School</label>
              <select value={form.target_school_id} onChange={e => setForm(f => ({ ...f, target_school_id: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
                <option value="">Select school...</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Date & Time *</label>
              <input type="datetime-local" value={form.scheduled_at}
                onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none"
                data-testid="comp-datetime-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Duration (min)</label>
              <input type="number" min={5} max={180} value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Questions</label>
              <input type="number" min={5} max={100} value={form.question_count}
                onChange={e => setForm(f => ({ ...f, question_count: Number(e.target.value) }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none" />
            </div>
            {!form.is_free && (
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">Entry Fee (₦)</label>
                <input type="number" min={0} value={form.entry_fee}
                  onChange={e => setForm(f => ({ ...f, entry_fee: Number(e.target.value) }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none"
                  data-testid="comp-fee-input" />
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <button onClick={() => setForm(f => ({ ...f, requires_camera: !f.requires_camera }))}
              className={`flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border transition-colors ${form.requires_camera ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
              data-testid="comp-camera-toggle">
              <Camera className="w-4 h-4" /> {form.requires_camera ? 'Camera On' : 'Camera Off'}
            </button>
            <button onClick={() => setForm(f => ({ ...f, is_free: !f.is_free, entry_fee: f.is_free ? 500 : 0 }))}
              className={`flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border transition-colors ${!form.is_free ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
              data-testid="comp-paid-toggle">
              <CreditCard className="w-4 h-4" /> {form.is_free ? 'Free Entry' : 'Paid Entry'}
            </button>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#2563eb] hover:bg-[#1d58d4] text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="comp-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MasterCompetitionsPage() {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [going, setGoing] = useState('');
  const [payCfg, setPayCfg] = useState({ paystack_public_key: '', paystack_secret_key_preview: '' });
  const [showPayCfg, setShowPayCfg] = useState(false);
  const [payForm, setPayForm] = useState({ paystack_public_key: '', paystack_secret_key: '' });
  const [savingPay, setSavingPay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, pr] = await Promise.allSettled([
        api.get('/quiz/master/competitions'),
        api.get('/master/competition-payment-config'),
      ]);
      if (cr.status === 'fulfilled' && cr.value?.success) setComps(cr.value.data || []);
      if (pr.status === 'fulfilled' && pr.value?.success) {
        setPayCfg(pr.value.data);
        setPayForm(f => ({ ...f, paystack_public_key: pr.value.data.paystack_public_key || '' }));
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const goLive = async (id) => {
    setGoing(id);
    try {
      const res = await api.post(`/quiz/master/competitions/${id}/go-live`);
      if (res?.success) { toast.success('Competition is now LIVE!'); load(); }
    } catch { toast.error('Failed to go live'); }
    setGoing('');
  };

  const savePayCfg = async () => {
    setSavingPay(true);
    try {
      const res = await api.put('/master/competition-payment-config', payForm);
      if (res?.success) { toast.success('Payment config saved'); setShowPayCfg(false); load(); }
    } catch { toast.error('Failed to save'); }
    setSavingPay(false);
  };

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2" data-testid="master-competitions-title">
            <Trophy className="w-7 h-7 text-amber-500" /> Competitions
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Schedule and manage inter-school quiz competitions</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPayCfg(true)}
            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2.5 rounded-xl transition-colors text-sm"
            data-testid="pay-config-btn">
            <CreditCard className="w-4 h-4" /> {payCfg.has_paystack_secret ? 'Paystack Configured' : 'Setup Paystack'}
          </button>
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d58d4] text-white font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
            data-testid="add-competition-btn">
            <Plus className="w-4 h-4" /> Schedule Competition
          </button>
        </div>
      </div>

      {/* Payment Config Panel */}
      {showPayCfg && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#2563eb]" /> Paystack Config (Competition Payments)</h3>
            <button onClick={() => setShowPayCfg(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Public Key</label>
              <input value={payForm.paystack_public_key} onChange={e => setPayForm(f => ({ ...f, paystack_public_key: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="pk_test_..." data-testid="paystack-pub-key-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Secret Key</label>
              <input type="password" value={payForm.paystack_secret_key} onChange={e => setPayForm(f => ({ ...f, paystack_secret_key: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="sk_test_..." data-testid="paystack-sec-key-input" />
            </div>
          </div>
          <button onClick={savePayCfg} disabled={savingPay}
            className="flex items-center gap-2 bg-[#2563eb] text-white font-bold px-5 py-2.5 rounded-xl text-sm disabled:opacity-50">
            {savingPay ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Config
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : comps.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
          <Trophy className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-bold">No competitions scheduled</p>
          <p className="text-sm text-slate-400 mt-1">Schedule your first competition!</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-50">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{comps.length} Competitions</p>
          </div>
          <div className="divide-y divide-slate-50">
            {comps.map(c => (
              <div key={c.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50" data-testid={`comp-row-${c.id}`}>
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${c.status === 'live' ? 'bg-green-100' : 'bg-amber-100'}`}>
                  <Trophy className={`w-5 h-5 ${c.status === 'live' ? 'text-green-600' : 'text-amber-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{c.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap text-xs text-slate-400">
                    <span>{c.subject}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(c.scheduled_at).toLocaleString('en-NG')}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.scope}</span>
                    {c.requires_camera && <span className="flex items-center gap-1 text-amber-600"><Camera className="w-3 h-3" /> Camera</span>}
                    {!c.is_free && <span className="flex items-center gap-1 text-emerald-600"><DollarSign className="w-3 h-3" /> ₦{(c.entry_fee || 0).toLocaleString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLOR[c.status] || 'bg-slate-100 text-slate-600'}`}>
                    {c.status?.toUpperCase()}
                  </span>
                  {c.status === 'upcoming' && (
                    <button onClick={() => goLive(c.id)} disabled={going === c.id}
                      className="flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-100 hover:bg-green-200 px-3 py-1.5 rounded-xl disabled:opacity-50"
                      data-testid={`go-live-${c.id}`}>
                      {going === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      Go Live
                    </button>
                  )}
                  <button onClick={() => setModal(c)} className="text-slate-400 hover:text-[#2563eb] p-2 rounded-xl hover:bg-blue-50" data-testid={`edit-comp-${c.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <CompModal
          comp={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
